import { store } from '@redux/store';
import { fromEvent } from 'rxjs';
import ModalStep from '../enums/modalStep';
import { setModalStep } from '../redux/slices';

export default class IframeVerification {
  iframeOrigin = null;

  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.containerSelector = 'data-iframe-container-verification';
    this.previousStep = ModalStep.IdentityVerification;
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
    // get the iframe origin url from the root component
    // to filter out messages not from jumio
    const url = new URL(this.rootComponent.verificationInfo.redirectUrl);
    this.iframeOrigin = url.origin;
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application's state.
   *
   */
  handleSecondaryAction() {
    IframeVerification.setModalStep(this.previousStep);
  }

  /**
   * Handles the DOM update event and sets up events if the current step is IframeVerification.
   *
   * @param {CustomEvent} event - The DOM update event containing details about the current step.
   * @param {Object} event.detail - The detail object of the event.
   * @param {string} event.detail.step - The current step in the modal process.
   */
  onDomUpdate(event) {
    if (event.detail && event.detail.step === ModalStep.IframeVerification) {
      this.setUpEvents();
    }
  }

  /**
   * Handles incoming messages from an iframe and processes the data.
   * If the message indicates a successful operation, it unsubscribes from the iframe listener
   * and triggers user identification verification. If an error occurs during processing,
   * it unsubscribes from the listener, updates the error state, and dispatches an action
   * to set the modal step to identity verification.
   *
   * @param {MessageEvent} event - The message event received from the iframe.
   */
  receiveMessage(event) {
    // trying to filter out messages not from jumio
    if (event.origin !== this.iframeOrigin) {
      return;
    }
    try {
      const data = JSON.parse(event.data);
      if (data.payload?.value === 'success') {
        this.iframeListenerSubscriber.unsubscribe();
        IframeVerification.setModalStep(ModalStep.VerificationInProgress);
      }
    } catch {
      this.iframeListenerSubscriber.unsubscribe();
      this.presentErrorState();
      IframeVerification.setModalStep(ModalStep.IdentityVerification);
    }
  }

  /**
   * Sets up event listeners for the iframe verification component.
   */
  setUpEvents() {
    this.receiveMessageCallback = this.receiveMessage.bind(this);
    this.iframeListenerSubscriber = fromEvent(window, 'message').subscribe(this.receiveMessageCallback);
  }

  /**
   * Renders the verification component for the self-guided tour.
   *
   * @returns {string} The HTML string for the verification component, including a heading
   * and an iframe for identification verification.
   */
  render() {
    const { redirectUrl } = this.rootComponent.verificationInfo;
    return `
      <div class="nhs-c-self-guided-left-panel">
        <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Verify identification</h1>
        <iframe class='nhs-c-self-guided-modal__iframe' src="${redirectUrl}" allow="camera"></iframe>
      </div>
    `;
  }

  /**
   * Updates the error state in the root component.
   */
  presentErrorState() {
    this.rootComponent.presentErrorState$.next(true);
  }

  /**
   * Dispatches an action to update the modal step in the application's state.
   *
   * @param {ModalStep} step - The modal step to set in the application's state.
   */
  static setModalStep(step) {
    store.dispatch(setModalStep({ modalStep: step }));
  }
}
